import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Card, Col, Collapse, Container, Table } from 'react-bootstrap'
import Avatar from '../components/common/Avatar'
import { useAuth } from '../contexts/auth'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useToasts } from '../contexts/toasts';
import { Authentication } from '../api/Authentication';
import Cookies from 'js-cookie';
import keycloak from '../keycloak';
import IconButton from '../components/common/IconButton';
import { Select, Tag, Tooltip } from 'antd';
import { Organization } from '../models/ControlPanel';
import { ControlPanelManager } from '../api/ControlPanelManager';
import { DefaultOptionType } from 'antd/es/select';
import { useFullScreenLoader } from '../contexts/fullScreenLoader';

export default function Account() {
  const { loggedUser, token, setToken } = useAuth();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>();
  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const toggleChangePassword = useCallback(() => setShowChangePassword(v => !v), []);

  const userOrganizations = useMemo(() => loggedUser?.groups?.filter(g => g.startsWith("/Organizations")).map(g => g.replace("/Organizations/", "")) || [], [loggedUser]);
  const userPermissions = useMemo(() => loggedUser?.groups?.filter(g => g.startsWith("/Permissions")).map(g => g.replace("/Permissions/", "")) || [], [loggedUser]);
  const remainingGroups = useMemo(() => loggedUser?.groups?.filter(g => !g.startsWith("/Permissions") && !g.startsWith("/Organizations")) || [], [loggedUser]);

  const initOrganizations = useCallback(() => {
    ControlPanelManager.getUserOrganizations(loggedUser?.sub as string)
      .then(
        ({ data }) => setOrganizations(data),
        (err) => {
          setOrganizations([]);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('organizations-error', message, `Organizations Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [loggedUser, showToast]);

  useEffect(() => {
    if (loggedUser) initOrganizations();
    // eslint-disable-next-line
  }, [loggedUser]);

  const currentOrganizationIcon = useCallback((organization: string) => {
    return organization === loggedUser?.userOrganizationStatus?.current.name
      ? (
        <Tooltip title="Current Organization">
          <FontAwesomeIcon icon={regular('check-circle')} className="text-success me-1" />
        </Tooltip>
      )
      : null;
  }, [loggedUser]);

  const handleRefreshToken = useCallback(async () => {
    try {
      const { data } = await Authentication.refreshToken();
      setToken(data.access_token);
      Cookies.set(window.__env.AUTH_TOKEN_NAME || "alida-token", data.access_token, { expires: data.expires_in, secure: true });
      Cookies.set(window.__env.AUTH_REFRESH_TOKEN_NAME || "alida-refresh-token", data.refresh_token, { expires: data.refresh_expires_in, secure: true });
      showToast('refresh-token-success', "Token refreshed successfully", "Refresh Token Success", 'success');
    } catch (err: any) {
      const message = err.response?.data?.message || err.message;
      const status = err.response?.status;
      showToast('refresh-token-error', message, `Refresh Token Error ${status ? `(${status})` : ''}`, 'danger');
    }
  }, [setToken, showToast]);

  const handleTokenCopy = useCallback(() => {
    navigator.clipboard.writeText(token!);
    showToast("token-copy", "Token copied to clipboard");
  }, [token, showToast]);

  const changePassword = useCallback(() => keycloak.login({ action: "UPDATE_PASSWORD" }), []);

  const changeOrganization = useCallback((_: any, option?: DefaultOptionType) => {
    showLoader();
    const body: Organization = { id: option!.value as string, name: option!.label as string };

    ControlPanelManager.changeOrganization(loggedUser!.sub, body)
      .then(
        async () => {
          await handleRefreshToken();
          window.location.reload();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('change-organization-error', message, `Organization Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showLoader, hideLoader, loggedUser, showToast, handleRefreshToken]);

  return loggedUser ? (
    <Container className="mt-3">
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex align-items-center mb-3">
            <Avatar user={loggedUser} />
            <Col className="ms-2">
              <h5 className="mb-0">{loggedUser.preferred_username}</h5>
              <span className="text-muted">{loggedUser.email}</span>
            </Col>
            <Col md={2}>
              <Tooltip title="Change Organization" mouseEnterDelay={1}>
                <Select
                  className="w-100"
                  placeholder="Select current organization"
                  prefix={<FontAwesomeIcon icon={solid('users')} />}
                  value={organizations ? loggedUser.userOrganizationStatus?.current.id : loggedUser.userOrganizationStatus?.current.name}
                  options={organizations?.map(org => ({ value: org.id, label: org.name }))}
                  loading={!organizations}
                  onChange={changeOrganization}
                />
              </Tooltip>
            </Col>
            <Button variant="light" className="ms-2 border" size="sm" onClick={handleRefreshToken}>
              <FontAwesomeIcon icon={solid('arrow-rotate-right')} />
              <span className="ms-2">Refresh user token</span>
            </Button>
            <Button variant="light" className="ms-2 border" size="sm" onClick={handleTokenCopy}>
              <FontAwesomeIcon icon={regular('clipboard')} />
              <span className="ms-2">Copy user token</span>
            </Button>
          </div>

          <Table className="table-transparent mb-0">
            <thead>
              <tr>
                <th style={{ width: '40%' }}></th>
                <td></td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="col">Name</th>
                <td className="text-end">{loggedUser.name}</td>
              </tr>
              {userOrganizations.length > 0 && (
                <tr>
                  <th scope="col">Organizations</th>
                  <td className="text-end">{userOrganizations.map((organization, index) => <Tag key={index} className="m-0 ms-1" color="blue" icon={currentOrganizationIcon(organization)}>{organization}</Tag>)}</td>
                </tr>
              )}
              {userPermissions.length > 0 && (
                <tr>
                  <th scope="col">Permissions</th>
                  <td className="text-end">{userPermissions.map((permission, index) => <Tag key={index} className="m-0 ms-1" color="gold">{permission}</Tag>)}</td>
                </tr>
              )}
              {remainingGroups.length > 0 && (
                <tr>
                  <th scope="col">Groups</th>
                  <td className="text-end">{remainingGroups.map((group, index) => <Tag key={index} className="m-0 ms-1" color="purple">{group}</Tag>)}</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body className="p-1">
          <button className="d-block w-100 text-start toggle-button" onClick={toggleChangePassword}>
            <FontAwesomeIcon icon={solid('key')} />
            <span className="ms-2">Change Password</span>
          </button>

          <Collapse in={showChangePassword}>
            <div>
              <div className="p-3 text-center">
                If you want to set a new password <IconButton className="p-0 fw-bold text-primary" onClick={changePassword}>click here</IconButton>
              </div>
            </div>
          </Collapse>
        </Card.Body>
      </Card>
    </Container>
  ) : null;
}
